import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import {
  fetchMeetingFavourites,
  removeMeetingFavourite,
  invalidateMeetFavCache,
} from '../../utils/wishlistAPI';
import { getApiUrl } from '../../config/api';
import './Favourite.css';

const Favourite = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const loadFavourites = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      localStorage.setItem('authRedirectPath', '/meetings/favourite');
      navigate('/login');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      invalidateMeetFavCache();
      const items = await fetchMeetingFavourites();
      setRooms(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error('[Favourite] load error:', err);
      setError('Failed to load favourites. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { loadFavourites(); }, [loadFavourites]);

  const handleRemove = async (roomId) => {
    setRemovingId(String(roomId));
    setRooms((prev) => prev.filter((r) => String(r._id || r.roomId) !== String(roomId)));
    try {
      await removeMeetingFavourite(roomId);
    } catch (err) {
      console.error('[Favourite] remove error:', err);
      loadFavourites();
    } finally {
      setRemovingId(null);
    }
  };

  const getFirstImage = (room) => {
    const imgs = room.images || [];
    if (!imgs.length) return null;
    const src = typeof imgs[0] === 'string' ? imgs[0] : (imgs[0]?.url || '');
    if (!src) return null;
    if (src.startsWith('http')) return src;
    return getApiUrl(src.startsWith('/') ? src : `/${src}`);
  };

  if (loading) {
    return (
      <div className="fav-page">
        <Navbar />
        <div className="fav-loading">
          <div className="fav-spinner" />
          <p>Loading your favourites…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fav-page">
      <Navbar />
      <div className="fav-container">

        {/* Header */}
        <div className="fav-header">
          <button className="fav-back-btn" onClick={() => navigate(-1)}>
            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Go Back
          </button>
          <div className="fav-title-row">
            <h1>Favourite Rooms</h1>
            {rooms.length > 0 && (
              <span className="fav-count">{rooms.length} room{rooms.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          <p className="fav-subtitle">Your preferred meeting rooms — synced across all devices</p>
        </div>

        {error && (
          <div className="fav-error">
            <span>{error}</span>
            <button onClick={loadFavourites}>Retry</button>
          </div>
        )}

        {/* Empty state */}
        {!error && rooms.length === 0 && (
          <div className="fav-empty">
            <div className="fav-empty-icon">❤️</div>
            <h2>No Favourite Rooms Yet</h2>
            <p>Tap the heart icon on any meeting room to save it here for quick access.</p>
            <button className="fav-browse-btn" onClick={() => navigate('/meeting-book')}>
              Browse Meeting Rooms
            </button>
          </div>
        )}

        {/* Grid */}
        {rooms.length > 0 && (
          <div className="fav-grid">
            {rooms.map((room) => {
              const roomId = room._id || room.roomId || room.id;
              const imgSrc = getFirstImage(room);
              const isRemoving = removingId === String(roomId);

              return (
                <div key={roomId} className={`fav-card${isRemoving ? ' removing' : ''}`}>
                  {/* Image */}
                  <div className="fav-card-img-wrap">
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={room.name}
                        className="fav-card-img"
                        onError={(e) => { e.target.parentElement.classList.add('no-img'); }}
                      />
                    ) : (
                      <div className="fav-card-no-img">🏢</div>
                    )}
                    <button
                      className="fav-card-heart"
                      onClick={() => handleRemove(roomId)}
                      disabled={isRemoving}
                      title="Remove from favourites"
                    >
                      ❤️
                    </button>
                  </div>

                  {/* Info */}
                  <div className="fav-card-body">
                    <div className="fav-card-type">{room.roomType || 'Meeting Room'}</div>
                    <h3 className="fav-card-name">{room.name || '—'}</h3>

                    {(room.city || room.location || room.address) && (
                      <p className="fav-card-location">
                        📍 {[room.location, room.city, room.address].filter(Boolean).join(', ')}
                      </p>
                    )}

                    <div className="fav-card-meta">
                      {room.capacity > 0 && (
                        <span className="fav-meta-chip">👥 {room.capacity} people</span>
                      )}
                      {room.pricePerHour > 0 && (
                        <span className="fav-meta-chip">₹{room.pricePerHour}/hr</span>
                      )}
                    </div>

                    {Array.isArray(room.amenities) && room.amenities.length > 0 && (
                      <div className="fav-card-amenities">
                        {room.amenities.slice(0, 4).map((a, i) => (
                          <span key={i} className="fav-amenity-tag">{a}</span>
                        ))}
                        {room.amenities.length > 4 && (
                          <span className="fav-amenity-more">+{room.amenities.length - 4}</span>
                        )}
                      </div>
                    )}

                    <div className="fav-card-actions">
                      <button
                        className="fav-book-btn"
                        onClick={() => navigate(`/meetings/room/${roomId}`)}
                      >
                        Book Now
                      </button>
                      <button
                        className="fav-remove-btn"
                        onClick={() => handleRemove(roomId)}
                        disabled={isRemoving}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Favourite;
